import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RELOAD_KEY = "vixio.chunk-reload";

/**
 * Carrega um pedaço do app sob demanda. Se o arquivo não existe mais (deploy novo trocou os nomes com hash), recarrega a
 * página uma vez para pegar o índice atual, em vez de deixar a tela quebrada.
 */
export function loadChunk<T>(load: () => Promise<T>): Promise<T> {
  return load().then(
    (m) => {
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* navegação privada */
      }
      return m;
    },
    (err: unknown) => {
      let already = true;
      try {
        already = sessionStorage.getItem(RELOAD_KEY) === "1";
        if (!already) sessionStorage.setItem(RELOAD_KEY, "1");
      } catch {
        /* sem sessionStorage: não arrisca recarregar em laço */
      }
      if (!already && typeof window !== "undefined") {
        window.location.reload();
        return new Promise<T>(() => undefined);
      }
      throw err;
    },
  );
}

/**
 * Tela carregada sob demanda (code-splitting por rota). Use no registro do módulo:
 *   const PeoplePage = lazyPage(() => import("./PeoplePage"), "PeoplePage");
 *   routes: [{ path: "/pessoas", element: <PeoplePage />, minRole: "member" }]
 * O app envolve as rotas em <Suspense> com o esqueleto da página (PageSkeleton).
 * Só separa o arquivo se nada mais o importar de forma estática (o Vite avisa no build).
 */
export function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K): LazyExoticComponent<Extract<M[K], ComponentType<any>>> {
  return lazy(() => loadChunk(load).then((m) => ({ default: m[name] as Extract<M[K], ComponentType<any>> })));
}
